import React, { useMemo } from 'react';
import { NodeProps } from '@xyflow/react';
import BaseGroupNode from './BaseGroupNode';
import i18n from '../../../i18n';

// 기본 스타일 타입 정의
interface NodeStyle {
  width?: number;
  height?: number;
  [key: string]: any;
}

// NodeProps 확장 타입 정의
interface ExtendedNodeProps extends NodeProps {
  style?: NodeStyle;
}

const GroupNode: React.FC<ExtendedNodeProps> = ({ id, data, type, selected, style, dragging, draggable, selectable, deletable, zIndex = 0, ...props }) => {
  // 노드 타입에 따라 설정
  let defaultLabel = 'Group';
  let headerColor = '#3b3b3b';
  let description = '';
  
  // 기본 크기 설정 (useMemo로 최적화)
  const defaultStyle = useMemo(() => {
    // 노드 타입별 기본 크기 설정
    const dataStyle = data?.style as NodeStyle || {};
    
    if (type === 'subflow') {
      return { 
        width: dataStyle.width || 320, 
        height: dataStyle.height || 240 
      };
    } else if (type === 'repeatGroup') {
      return { 
        width: dataStyle.width || 280, 
        height: dataStyle.height || 220 
      };
    }
    
    // 일반 그룹 노드 기본 크기
    return { 
      width: dataStyle.width || 300, 
      height: dataStyle.height || 240 
    };
  }, [type, data?.style]);
  
  // 서브플로우 노드 설정
  if (type === 'subflow') {
    defaultLabel = 'Subflow';
    headerColor = '#4a9eff';
    description = i18n.t('workflow:group.subflowDesc');
  }
  
  // 반복그룹 노드 설정
  if (type === 'repeatGroup') {
    defaultLabel = 'Repeat Group';
    headerColor = '#ff9d4a';
    description = i18n.t('workflow:group.repeatDesc');
  }

  // 디버깅용 로그
  console.log(`GroupNode 렌더링 - ${id}, 타입: ${type}`, {
    style,
    defaultStyle,
    dataStyle: data?.style,
    width: style?.width || defaultStyle.width,
    height: style?.height || defaultStyle.height
  });

  // Calculate the width and height
  const nodeWidth = style?.width || defaultStyle.width;
  const nodeHeight = style?.height || defaultStyle.height;

  return (
    <BaseGroupNode
      id={id}
      data={{
        ...data,
        defaultWidth: defaultStyle.width,
        defaultHeight: defaultStyle.height,
        // Pass style properties through data
        headerColor,
        defaultLabel,
        description,
        style: {
          width: nodeWidth,
          height: nodeHeight
        }
      }}
      type={type}
      selected={selected}
      // Add explicit width and height as required by ReactFlow
      width={nodeWidth}
      height={nodeHeight}
      // Add all required ReactFlow node properties
      dragging={dragging || false}
      draggable={draggable !== false}
      selectable={selectable !== false}
      deletable={deletable !== false}
      zIndex={zIndex}
      {...props}
    />
  );
};

export default GroupNode; 